/**
 * useIRHeightFeedback — Step 8.4 React glue.
 *
 * After the first-pass IR layout (step 8.2) positions cards using default
 * leaf heights, the DOM renders and each card's actual CSS height becomes
 * measurable. This hook reads those measurements, bucket-rounds them per
 * Rule S-G.a ({100, 110, 120, 130, 140, 150}), and re-runs the pipeline via
 * `relayoutDAGviaIR(laid, heightMap, nodes)`. When the resulting geometry
 * differs from what's already in state, the hook calls `setNodes` with the
 * updated array. Subsequent renders paint the height-corrected layout.
 *
 * DESIGN DECISIONS
 * ────────────────
 *  * DOM-agnostic — the caller owns how to find a node's element (via refs,
 *    data-attributes, whatever). The hook takes `getNodeElement(id) → Element`.
 *  * Thrash-safe — the hook compares the new heightMap against the last one
 *    it applied. If they bucket-match, it skips setNodes entirely. Paired
 *    with the idempotence of `relayout()` this guarantees the feedback loop
 *    terminates in at most two passes.
 *  * Flag-gated — if `enabled` is false (IR pipeline off), the hook is a
 *    complete no-op. Zero cost, zero risk of interfering with the legacy
 *    Sugiyama path.
 *  * Fallback-safe — if `relayoutDAGviaIR` returns null (malformed laid
 *    tree, pipeline throw), the hook silently keeps the first-pass geometry.
 *    Missing feedback is invisible; a crashed effect would break the canvas.
 *
 * USAGE
 * ─────
 *   // In CanvasPage.jsx:
 *   const laidRef = useRef(null);
 *   const nodeElsRef = useRef(new Map());
 *   const firstPass = runLayout(nodes, conns, {
 *     ...opts,
 *     onLaid: (w) => { laidRef.current = w; },
 *   });
 *   // ...setNodes(firstPass)
 *   useIRHeightFeedback({
 *     enabled: isIRPipelineEnabled(),
 *     laid: laidRef.current,
 *     nodes: canvas.nodes,
 *     getNodeElement: (id) => nodeElsRef.current.get(id),
 *     setNodes: canvas.setNodes,
 *   });
 *   // ...<CanvasNode ref={(el) => nodeElsRef.current.set(node.id, el)} ... />
 */

import { useEffect, useRef } from 'react';
import { relayoutDAGviaIR, bucketHeight } from '../utils/irAdapter';

// ───────────────────────────────────────────────────────────────────
//  Pure helpers (unit-testable without React)
// ───────────────────────────────────────────────────────────────────

/**
 * Build an id → bucketed pixel height map from the current DOM state.
 * Skips ids whose element resolver returns null/undefined (card not mounted
 * yet — happens on the very first paint of a new workflow).
 *
 * @param {Array<{id: string}>} nodes
 * @param {(id: string) => { getBoundingClientRect?: () => {height: number} } | null} getNodeElement
 * @returns {Object.<string, number>}
 */
export function collectHeightMap(nodes, getNodeElement) {
  const out = {};
  if (!Array.isArray(nodes) || typeof getNodeElement !== 'function') return out;
  for (const n of nodes) {
    const el = getNodeElement(n.id);
    if (!el || typeof el.getBoundingClientRect !== 'function') continue;
    const rect = el.getBoundingClientRect();
    if (!rect || !Number.isFinite(rect.height) || rect.height <= 0) continue;
    out[n.id] = bucketHeight(rect.height);
  }
  return out;
}

/**
 * Compare two height maps. Returns true iff the keys are the same and every
 * value matches. Missing entries in either map count as different — this is
 * the right behaviour when the node set changed (added or removed nodes
 * must trigger a relayout even if every measured card kept its height).
 *
 * @param {Object.<string, number>} a
 * @param {Object.<string, number>} b
 * @returns {boolean}
 */
export function heightMapsEqual(a, b) {
  if (a === b) return true;
  if (!a || !b) return false;
  const ak = Object.keys(a);
  const bk = Object.keys(b);
  if (ak.length !== bk.length) return false;
  for (const k of ak) {
    if (a[k] !== b[k]) return false;
  }
  return true;
}

// ───────────────────────────────────────────────────────────────────
//  The hook
// ───────────────────────────────────────────────────────────────────

/**
 * Run the IR height-feedback second pass after each layout update.
 *
 * @param {object} params
 * @param {boolean} params.enabled         Flag gate. `false` → no-op hook.
 * @param {object|null} params.laid        Laid-out IR tree from `onLaid`.
 * @param {Array<{id: string}>} params.nodes  Current nodes state.
 * @param {(id: string) => Element|null} params.getNodeElement
 *   Caller's bridge from node id to its mounted DOM element.
 * @param {(nextNodes: object[]) => void} params.setNodes
 *   State setter; called only when the relaid geometry differs from the
 *   current positions.
 */
export function useIRHeightFeedback(params) {
  const {
    enabled,
    laid,
    nodes,
    getNodeElement,
    setNodes,
  } = params;

  // Track the last heightMap we actually applied, so repeat measurements
  // that bucket to the same values don't trigger a state churn.
  const lastAppliedRef = useRef(null);

  useEffect(() => {
    if (!enabled || !laid || !Array.isArray(nodes) || nodes.length === 0) return;

    const heightMap = collectHeightMap(nodes, getNodeElement);
    // Empty map → nothing mounted yet, or feedback is disabled in this tree.
    if (Object.keys(heightMap).length === 0) return;

    if (heightMapsEqual(heightMap, lastAppliedRef.current)) return;

    const positioned = relayoutDAGviaIR(laid, heightMap, nodes);
    if (!positioned) {
      // Pipeline bailed; leave first-pass geometry alone.
      return;
    }

    // Skip the state update when geometry didn't actually shift (cards
    // happened to already sit at their bucketed-height positions).
    const noChange = positioned.every((p, i) => {
      const cur = nodes[i];
      return cur && cur.x === p.x && cur.y === p.y;
    });
    if (noChange) {
      lastAppliedRef.current = heightMap;
      return;
    }

    lastAppliedRef.current = heightMap;
    setNodes(positioned);
  }, [enabled, laid, nodes, getNodeElement, setNodes]);
}
